import { Immutable } from "immer";
import React from "react";

// Context for passing image load handler to nested tile components
const ImageLoadContext = React.createContext<(() => void) | undefined>(
    undefined,
);

/**
 * Hook that captures the latest ImageLoadContext callback in a ref,
 * avoiding stale closures without triggering re-renders.
 */
export const useImageLoadRef = () => {
    const onImageLoad = React.useContext(ImageLoadContext);
    const onImageLoadRef = React.useRef(onImageLoad);
    React.useEffect(() => {
        onImageLoadRef.current = onImageLoad;
    }, [onImageLoad]);
    return onImageLoadRef;
};

type IProps = Immutable<{
    tileUID: string | number;
    ariaLabel: string;
    children?: React.ReactNode;
}> & {
    // Refs cannot be Immutable (they are mutable by nature)
    forwardedRef: React.RefObject<HTMLDivElement | null>;
    primaryLinkRef: React.RefObject<HTMLAnchorElement | null>;
};

const clickPrimaryLink = (
    primaryLinkRef: React.RefObject<HTMLAnchorElement | null>,
) => {
    if (primaryLinkRef.current) {
        primaryLinkRef.current.click();
    }
};

export const GridTile = (props: IProps): React.ReactNode => {
    const [isLoaded, setIsLoaded] = React.useState(false);

    const handleImageLoad = React.useCallback(() => {
        setIsLoaded(true);
    }, []);

    const tileClasses = [
        "post",
        `al-tlovegrid-tile-${props.tileUID}`,
        !isLoaded && "post--loading",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <ImageLoadContext.Provider value={handleImageLoad}>
            <article
                ref={props.forwardedRef}
                className={tileClasses}
                role="group"
                aria-label={props.ariaLabel}
                aria-busy={!isLoaded}
            >
                <section
                    className="post__content"
                    onClick={(e) => {
                        // Skip delegated navigation for nested links/buttons to avoid
                        // recursive clicks and hijacking category link navigation.
                        if (
                            e.target instanceof Element &&
                            e.target.closest("a, button")
                        ) {
                            return;
                        }
                        clickPrimaryLink(props.primaryLinkRef);
                    }}
                >
                    {props.children}
                </section>
            </article>
        </ImageLoadContext.Provider>
    );
};
